import React, { useState } from 'react';
import AdminPanel from './AdminPanel';
import './App.css';

function App() {
  const [personalnummer, setPersonalnummer] = useState('');
  const [name, setName] = useState('');
  const [userId, setUserId] = useState<number|null>(null);
  const [modules, setModules] = useState<any[]>([]);
  const [selectedModule, setSelectedModule] = useState<number|null>(null);
  const [pdfs, setPdfs] = useState<any[]>([]);
  const [progress, setProgress] = useState<any[]>([]);
  const [testVisible, setTestVisible] = useState(false);
  const [testAnswers, setTestAnswers] = useState<number[]>([]);
  const [testResult, setTestResult] = useState<boolean|null>(null);
  const [testQuestions, setTestQuestions] = useState<any[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [completedModules, setCompletedModules] = useState<Set<number>>(new Set());
  const [totalPdfsCount, setTotalPdfsCount] = useState<number>(0);

  // Anmeldung
  const handleRegister = async () => {
    if (!agreedToTerms) {
      alert('Bitte bestätigen Sie die Nutzungsbedingungen!');
      return;
    }
    const res = await fetch('/api/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ personalnummer, name })
    });
    const data = await res.json();
    setUserId(data.id);
    
    // Lade Module
    const modulesRes = await fetch('/api/modules');
    const modulesData = await modulesRes.json();
    setModules(modulesData);
    
    // Lade bisherigen Fortschritt
    const progressRes = await fetch(`/api/user/${data.id}/progress`);
    const progressData = await progressRes.json();
    const actualProgress = progressData.progress || progressData;
    setProgress(actualProgress);
    
    // Lade ALLE PDFs um zu prüfen welche Module komplett sind
    const allPdfsPromises = modulesData.map((mod: any) => 
      fetch(`/api/module/${mod.id}/pdfs`).then(r => r.json())
    );
    const allPdfsArrays = await Promise.all(allPdfsPromises);
    const allPdfs = allPdfsArrays.flat();
    
    // Speichere Gesamtzahl der PDFs
    setTotalPdfsCount(allPdfs.length);
    
    // Prüfe welche Module komplett sind
    const completed = new Set<number>();
    modulesData.forEach((mod: any, index: number) => {
      const modulePdfs = allPdfsArrays[index];
      if (modulePdfs.length > 0 && modulePdfs.every((pdf: any) => actualProgress.find((p: any) => p.pdf_id === pdf.id)?.read === 1)) {
        completed.add(mod.id);
      }
    });
    setCompletedModules(completed);
    
    // Prüfe ob Test bereits bestanden
    const testRes = await fetch(`/api/user/${data.id}/test-status`);
    const testData = await testRes.json();
    if (testData.passed) {
      setTestResult(true);
    }
  };

  // PDFs abrufen
  const fetchPdfs = async (moduleId: number) => {
    setSelectedModule(moduleId);
    const res = await fetch(`/api/module/${moduleId}/pdfs`);
    const data = await res.json();
    setPdfs(data);
    
    // Fortschritt aktualisieren
    const progressRes = await fetch(`/api/user/${userId}/progress`);
    const progressData = await progressRes.json();
    const actualProgress = progressData.progress || progressData;
    setProgress(actualProgress);
    
    // Prüfe ob dieses Modul komplett ist
    const allRead = data.every((pdf: any) => actualProgress.find((p: any) => p.pdf_id === pdf.id)?.read === 1);
    if (allRead && data.length > 0) {
      setCompletedModules(prev => new Set(prev).add(moduleId));
    }
    
    // Prüfe ALLE Module erneut um sicherzustellen dass grüne Markierung erhalten bleibt
    await updateAllCompletedModules(actualProgress);
  };

  // Fortschritt abrufen und alle Module aktualisieren
  const updateAllCompletedModules = async (actualProgress?: any[]) => {
    const progressToUse = actualProgress || progress;
    
    const completed = new Set<number>();
    for (const mod of modules) {
      const res = await fetch(`/api/module/${mod.id}/pdfs`);
      const modulePdfs = await res.json();
      if (modulePdfs.length > 0 && modulePdfs.every((pdf: any) => progressToUse.find((p: any) => p.pdf_id === pdf.id)?.read === 1)) {
        completed.add(mod.id);
      }
    }
    setCompletedModules(completed);
  };

  // Fortschritt abrufen
  const fetchProgress = async (uid: number) => {
    const res = await fetch(`/api/user/${uid}/progress`);
    const data = await res.json();
    const actualProgress = data.progress || data; // Kompatibilität mit altem Format
    setProgress(actualProgress);
    
    // Aktualisiere alle Module
    await updateAllCompletedModules(actualProgress);
  };

  // Fortschritt manuell aktualisieren
  const handleRefreshProgress = async () => {
    if (!userId) return;
    
    const res = await fetch(`/api/user/${userId}/progress`);
    const data = await res.json();
    const actualProgress = data.progress || data;
    setProgress(actualProgress);
    
    // Aktualisiere alle Module
    await updateAllCompletedModules(actualProgress);
    
    // Aktualisiere auch die aktuell angezeigten PDFs falls ein Modul ausgewählt ist
    if (selectedModule) {
      const pdfRes = await fetch(`/api/module/${selectedModule}/pdfs`);
      const pdfData = await pdfRes.json();
      setPdfs(pdfData);
    }
  };

  // PDF in neuem Tab öffnen und Timer starten
  const handleShowPdf = (pdf: any) => {
    // Öffne PDF-Viewer in neuem Tab mit Timer
    window.open(`/pdf/${pdf.id}/${userId}`, '_blank');
  };

  // Test anzeigen, wenn alle PDFs gelesen
  const checkAllRead = () => {
    // Prüfe ob alle PDFs gelesen wurden (dynamisch basierend auf tatsächliche Anzahl)
    if (totalPdfsCount === 0) return false; // Noch keine PDFs geladen
    return progress.length >= totalPdfsCount && progress.every((p) => p.read === 1);
  };

  // Test absenden
  const handleTestSubmit = async () => {
    const response = await fetch(`/api/user/${userId}/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers: testAnswers })
    });
    const result = await response.json();
    setTestResult(result.passed);
    
    if (result.passed) {
      await fetch('/api/notify-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
    }
  };

  // Test starten und Fragen laden
  const handleStartTest = async () => {
    const res = await fetch('/api/questions');
    const questions = await res.json();
    setTestQuestions(questions); // Alle 11 Fragen laden
    setTestAnswers(Array(questions.length).fill(0));
    setTestVisible(true);
  };

  const [showAdmin, setShowAdmin] = useState(false);
  
  const handleAdminClick = () => {
    if (!showAdmin) {
      const password = prompt('Admin-Passwort eingeben:');
      if (password === '29111998') {
        setShowAdmin(true);
      } else if (password !== null) {
        alert('Falsches Passwort!');
      }
    } else {
      setShowAdmin(false);
    }
  };

  return (
    <div className="App">
      <img src="/logo.png" alt="Logo" className="app-logo" />
      <h1>Arbeitsschutzunterweisung</h1>
      <button className="admin-button" onClick={handleAdminClick}>
        {showAdmin ? 'Zurück' : 'Admin'}
      </button>
      {showAdmin ? (
        <AdminPanel />
      ) : (
        <>
        {!userId && (
          <div className="login-container">
            <h2>Anmeldung</h2>
            <input placeholder="Personalnummer" value={personalnummer} onChange={e => setPersonalnummer(e.target.value)} />
            <input placeholder="Vorname Nachname" value={name} onChange={e => setName(e.target.value)} />
            <div style={{margin: '20px 0', padding: '15px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107'}}>
              <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '0.9rem', lineHeight: '1.6'}}>
                <input 
                  type="checkbox" 
                  checked={agreedToTerms} 
                  onChange={e => setAgreedToTerms(e.target.checked)}
                  style={{marginRight: '10px', marginTop: '4px', flexShrink: 0, width: '18px', height: '18px', cursor: 'pointer'}}
                />
                <span style={{textAlign: 'justify'}}>
                  Hiermit versichere ich, alle Inhalte dieser Unterweisung sorgfältig und gewissenhaft zu lesen. 
                  Bei Unklarheiten oder Fragen werde ich mich an die zuständigen Beauftragten wenden. 
                  Den anschließenden Test werde ich eigenständig und nach bestem Wissen und Gewissen absolvieren.
                </span>
              </label>
            </div>
            <button onClick={handleRegister} disabled={!agreedToTerms} style={{opacity: agreedToTerms ? 1 : 0.5, cursor: agreedToTerms ? 'pointer' : 'not-allowed'}}>Anmelden</button>
            <p style={{fontSize: '0.85em', color: '#666', marginTop: '20px', lineHeight: '1.5', textAlign: 'justify'}}>
              Unterweisungen im Arbeitsschutz sind nach § 12 Arbeitsschutzgesetz (ArbSchG) und § 4 DGUV Vorschrift 1 verpflichtend. Sie müssen mindestens jährlich (für Jugendliche halbjährlich) arbeitsplatzbezogen, verständlich und dokumentiert erfolgen, um Gefahren abzuwenden. Die Unterweisung erfolgt bei Einstellung, Aufgabenänderung oder neuen Arbeitsmitteln und ist Führungsaufgabe.
            </p>
          </div>
        )}
        {userId && (
          <div className="main-container">
            <div className="modules-container">
              <h2>Module</h2>
              {modules.map(m => (
                <button 
                  className={`module-button ${completedModules.has(m.id) ? 'module-complete' : ''}`} 
                  key={m.id} 
                  onClick={() => fetchPdfs(m.id)}
                >
                  {m.name} {completedModules.has(m.id) && '✓'}
                </button>
              ))}
              <button 
                onClick={handleRefreshProgress}
                style={{
                  marginTop: '15px',
                  padding: '12px 20px',
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 'bold'
                }}
              >
                🔄 Fortschritt aktualisieren
              </button>
              <div style={{marginTop: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '10px', fontSize: '0.9rem', color: '#0d47a1', textAlign: 'center'}}>
                <strong>ℹ️ Bei Fragen oder Hilfe wenden Sie sich bitte an:</strong>
                <div style={{marginTop: '8px'}}>Ferhan Kocak oder Justin Lapok</div>
              </div>
            </div>
            {selectedModule && (
              <div className="pdf-list">
                <h3>PDFs in diesem Modul</h3>
                {pdfs.map(pdf => (
                  <div className="pdf-item" key={pdf.id}>
                    <span>{pdf.filename.replace(/^\d+-/, '').replace(/_/g, ' ').replace('.pdf', '')}</span>
                    <button onClick={() => handleShowPdf(pdf)}>Anzeigen</button>
                    {progress.find(p => p.pdf_id === pdf.id)?.read === 1 && <span style={{color: 'green', fontWeight: 'bold'}}>✔️ Gelesen</span>}
                  </div>
                ))}
              </div>
            )}
            {checkAllRead() && !testVisible && (
              <button onClick={handleStartTest}>Test starten</button>
            )}
            {testVisible && testQuestions.length > 0 && (
              <div className="test-container">
                <h3>Abschlusstest</h3>
                {testQuestions.map((q, i) => (
                  <div className="test-question" key={i}>
                    <p><strong>Frage {i + 1}:</strong> {q.question}</p>
                    <div className="answers-list">
                      {[1, 2, 3, 4].map(num => (
                        <div key={num} className="answer-item">
                          <label>
                            <input 
                              type="radio" 
                              name={`question-${i}`}
                              checked={testAnswers[i] === num}
                              onChange={() => {
                                const arr = [...testAnswers];
                                arr[i] = num;
                                setTestAnswers(arr);
                              }}
                            />
                            <span>{q[`answer${num}`]}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <button onClick={handleTestSubmit}>Test absenden</button>
                {testResult === true && <div className="test-result success">✔️ Test bestanden! Benachrichtigung wurde gesendet.</div>}
                {testResult === false && <div className="test-result error">❌ Test nicht bestanden. Bitte erneut versuchen.</div>}
              </div>
            )}
          </div>
        )}
        </>
      )}
    </div>
  );
}

export default App;
